import { cn } from "@/lib/utils";
import { friendlyPostableTitle } from "@/lib/utils";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { ConfigureExtras } from "./configure-extras";
import { PreviewCanvas } from "./preview-canvas";
import {
  type CategoryDisplayType,
  type PostableWithExtras,
  PreviewProps,
} from "./types";

interface PostablePreviewSheetProps {
  postable: PostableWithExtras;
  displayType: CategoryDisplayType;
  className?: string;
  onClose?: () => void;
  onUpdate?: (updatedPostable: PostableWithExtras) => void;
}

export function PostablePreviewSheet({
  postable: initialPostable,
  displayType,
  className,
  onClose,
  onUpdate,
}: PostablePreviewSheetProps) {
  // Track initialPostable changes with ref to avoid unnecessary re-renders
  const initialIdRef = useRef(initialPostable.id);

  // Create local state for the postable to manage changes
  const [postable, setPostable] = useState<PostableWithExtras>(() => ({
    ...initialPostable,
    json: initialPostable.json || {},
  }));

  // Only update from parent if initialPostable ID changes
  useEffect(() => {
    if (initialPostable.id !== initialIdRef.current) {
      initialIdRef.current = initialPostable.id;
      setPostable({
        ...initialPostable,
        json: initialPostable.json || {},
      });
    }
  }, [initialPostable]);

  // Create a stable callback that won't change on re-renders
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const updatePostableField = useCallback((field: string, value: any) => {
    setPostable((prev) => {
      // Skip update if value hasn't changed
      if (prev.json && prev.json[field] === value) {
        return prev;
      }

      // Create a new json object with the updated field
      const updatedJson = {
        ...(prev.json || {}),
        [field]: value,
      };

      // Return the new postable state
      return {
        ...prev,
        json: updatedJson,
      };
    });
  }, []);

  // Memoize props for child components to prevent unnecessary re-renders
  const configureExtrasProps = useMemo(
    () => ({
      postable,
      onUpdateField: updatePostableField,
      initialPostableId: initialPostable.id,
      onClose,
      onUpdate,
    }),
    [postable, updatePostableField, initialPostable.id, onClose, onUpdate],
  );

  const previewCanvasProps = useMemo(
    () => ({
      postable,
      displayType,
    }),
    [postable, displayType],
  );

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-1">
          Preview: {friendlyPostableTitle(postable.postable)}
        </h3>
        <p className="text-muted-foreground text-sm">
          This shows how the item will appear in your mobile feed
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <PreviewCanvas {...previewCanvasProps} />
      </div>

      <div className="mt-6 border-t pt-4">
        <ConfigureExtras {...configureExtrasProps} onClose={onClose} />
      </div>
    </div>
  );
}
